import type { CutWindow } from './buildSceneLayout';
import { easeInOut } from './resolveTransitionStyles';

/** Inside a camera-move window, tween the crop between the values the host's
 *  own resolver returns at the window edges. Host-agnostic: the editor
 *  preview resolves from cropCenterKeyframes, the render compositions from
 *  crop.scenes — both pass their resolver as `resolveAt`.
 *
 *  Camera-move windows that touch (a long move built from several ≤ 1.5 s
 *  steps) form one move: it eases in at the first window and out at the last
 *  and runs straight through the joints, instead of stopping at each one. A
 *  lone window eases exactly as before. */
export function resolveCameraMoveCrop<T extends { x: number; y: number; zoom: number }>(
  { frame, cuts, resolveAt }:
  { frame: number; cuts: CutWindow[]; resolveAt: (frame: number) => T | null },
): T | null {
  const moves = cuts
    .filter((cut) => cut.transition.kind === 'camera-move' && cut.endFrame > cut.startFrame)
    .sort((a, b) => a.startFrame - b.startFrame);
  const index = moves.findIndex((cut) => frame >= cut.startFrame && frame < cut.endFrame);
  if (index < 0) return null;
  // Windows are rounded to frames, so neighbors may overlap or gap by one.
  const touches = (a: CutWindow, b: CutWindow) => b.startFrame <= a.endFrame + 1;
  let first = index;
  let last = index;
  while (first > 0 && touches(moves[first - 1], moves[first])) first -= 1;
  while (last < moves.length - 1 && touches(moves[last], moves[last + 1])) last += 1;
  const start = moves[first].startFrame;
  const end = moves[last].endFrame;
  // Eased time along the whole move, then linear inside the window it lands in.
  const at = start + easeInOut((frame - start) / (end - start)) * (end - start);
  const chain = moves.slice(first, last + 1);
  const cut = chain.find((each) => at < each.endFrame) ?? chain[chain.length - 1];
  const a = resolveAt(cut.startFrame);
  const b = resolveAt(cut.endFrame);
  if (!a || !b) return null;
  const e = Math.min(1, Math.max(0, (at - cut.startFrame) / (cut.endFrame - cut.startFrame)));
  return {
    ...a,
    x: a.x + (b.x - a.x) * e,
    y: a.y + (b.y - a.y) * e,
    zoom: a.zoom + (b.zoom - a.zoom) * e,
  };
}
